import type { ApiError } from "@lms/shared-types";
import type { Session } from "../auth/auth";
import { isMock, type RuntimeConfig } from "../config";
import { mockApi } from "../mocks/fixtures";
import { ApiRequestError } from "./errors";

export { ApiRequestError };

/**
 * The last request that failed on this page, for the support form (PLT-09):
 * a "Save did nothing" report arrives with the id support needs to find the
 * log line, without asking anybody to find it. Held in memory for the tab,
 * never stored — the path is ours, the id is the gateway's.
 */
export interface ApiFailure {
  path: string;
  status: number;
  code: string;
  requestId?: string;
  at: string;
}
let lastFailure: ApiFailure | undefined;
export function getLastApiFailure(): ApiFailure | undefined {
  return lastFailure;
}
function failed(path: string, err: unknown): never {
  if (err instanceof ApiRequestError) lastFailure = { path: path.split("?")[0] ?? path, status: err.status, code: err.code, ...(err.requestId ? { requestId: err.requestId } : {}), at: new Date().toISOString() };
  throw err;
}

/**
 * The one place a request leaves the app: attaches the ID token, parses
 * the error shape (design.md §15), never a raw fetch from a component.
 * The API is same-origin (/api/*) so no CORS and no cross-site cookie.
 */
export async function fetchJson<T>(config: RuntimeConfig, session: Session, path: string, init?: RequestInit): Promise<T> {
  try {
    return await request<T>(config, session, path, init);
  } catch (err) {
    failed(path, err);
  }
}

async function request<T>(config: RuntimeConfig, session: Session, path: string, init?: RequestInit): Promise<T> {
  if (isMock) return mockApi<T>(session, path, init);
  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${session.idToken}`, accept: "application/json" },
  });
  if (res.ok) {
    if (res.status === 204) return undefined as T;
    // A 2xx that is not JSON is the edge answering for the API (the SPA shell, a maintenance page): say so, never
    // let a JSON parse error become "something went wrong" (the wiring rule, gairm/lms#203).
    if (!res.headers.get("content-type")?.includes("json")) throw new ApiRequestError(res.status, "NOT_JSON", "The service answered with something other than data; try again, and quote the time to support");
    return (await res.json()) as T;
  }
  let body: ApiError | undefined;
  try {
    body = (await res.json()) as ApiError;
  } catch {
    // no JSON body
  }
  throw new ApiRequestError(res.status, body?.error.code ?? "HTTP_ERROR", body?.error.message ?? `Request failed (${res.status})`, body?.error.request_id);
}

/**
 * A file the API produces (an export, a rendered document): the bytes and the
 * name from Content-Disposition. The mock answers JSON, which is wrapped as a
 * file so the download path is the same.
 */
export async function fetchFile(config: RuntimeConfig, session: Session, path: string): Promise<{ blob: Blob; filename: string; contentType: string }> {
  try {
    return await download(config, session, path);
  } catch (err) {
    failed(path, err);
  }
}

async function download(config: RuntimeConfig, session: Session, path: string): Promise<{ blob: Blob; filename: string; contentType: string }> {
  if (isMock) {
    const body = await mockApi<unknown>(session, path);
    return { blob: new Blob([JSON.stringify(body, null, 2)], { type: "application/json" }), filename: `${path.split("/").pop()?.split("?")[0] ?? "export"}.json`, contentType: "application/json" };
  }
  const res = await fetch(`${config.apiBaseUrl}${path}`, { headers: { authorization: `Bearer ${session.idToken}` } });
  if (!res.ok) {
    let body: ApiError | undefined;
    try {
      body = (await res.json()) as ApiError;
    } catch {
      // no JSON body
    }
    throw new ApiRequestError(res.status, body?.error.code ?? "HTTP_ERROR", body?.error.message ?? `Request failed (${res.status})`, body?.error.request_id);
  }
  const contentType = res.headers.get("content-type") ?? "application/octet-stream";
  if (contentType.includes("text/html")) throw new ApiRequestError(res.status, "NOT_A_FILE", "The service answered with a page instead of the file; try again, and quote the time to support");
  const disposition = res.headers.get("content-disposition") ?? "";
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? "download";
  return { blob: await res.blob(), filename, contentType };
}

